import { useEffect, useRef, useState } from 'react';
import { useCameraStore, cameraService } from '@/store/cameraStore';
import { safeStorage } from '@/utils/safeStorage';

interface BackgroundCameraProps {
    cameraReady: boolean;
    setCameraReady: (ready: boolean) => void;
    setDebugInfo: (info: string) => void;
}

// Toggle from the login screen by tapping the "build ..." stamp 5 times.
// Persisted in localStorage so it survives reloads; reading here is done once
// at module load, and the tap-handler reloads after flipping so the new
// value takes effect.
const SHOW_DEBUG_BADGE = ((): boolean => {
    try { return safeStorage.getItem('pinCamCameraDebug') === '1'; }
    catch { return false; }
})();

const BackgroundCamera = ({ cameraReady, setCameraReady, setDebugInfo }: BackgroundCameraProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    // Use individual selectors to avoid creating new objects on every render.
    // Each selector's state argument is inferred from the store's CameraState,
    // so these are fully typed without `any`.
    const startCamera = useCameraStore((state) => state.startCamera);
    const stream = useCameraStore((state) => state.stream);
    const isReady = useCameraStore((state) => state.isReady);
    const cameraError = useCameraStore((state) => state.cameraError);
    const facingMode = useCameraStore((state) => state.facingMode);
    const setVideoRef = useCameraStore((state) => state.setVideoRef);
    // Read camera-enumeration state for the debug badge below. Only the
    // counts + first few labels are surfaced — useful when triaging "why
    // doesn't the lens picker appear on my device".
    const availableDevices = useCameraStore((state) => state.availableDevices);
    const rearDevices = useCameraStore((state) => state.rearDevices);
    const frontDevices = useCameraStore((state) => state.frontDevices);
    const [hasPermission, setHasPermission] = useState<boolean | null>(null);
    // Force re-render for the debug badge so DOM-only fields (paused,
    // readyState, currentTime) stay fresh. No-op when the badge is off.
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!SHOW_DEBUG_BADGE) return;
        const id = window.setInterval(() => setTick(t => t + 1), 500);
        return () => window.clearInterval(id);
    }, []);

    // Effect 1: Check permissions only once on mount
    // iOS Safari does NOT support navigator.permissions.query() - it will crash
    // Instead, we optimistically attempt camera access - getUserMedia will handle permissions
    useEffect(() => {
        // Directly set permission to true - the actual permission check happens in getUserMedia
        setHasPermission(true);
    }, []); // Empty array, runs once

    // Effect 2: Set video ref in store when component mounts
    useEffect(() => {
        setVideoRef(videoRef);
    }, [setVideoRef]);

    // Effect 3: Start camera when permission is granted and video ref is ready
    useEffect(() => {
        if (hasPermission === true && videoRef.current) {
            // Give the video element a moment to be fully mounted
            const id = window.setTimeout(() => {
                startCamera();
            }, 100);
            return () => window.clearTimeout(id);
        } else if (hasPermission === false) {
            setDebugInfo('Camera permission denied');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission]);

    // Effect 4: Handle video stream and camera ready state
    useEffect(() => {
        if (videoRef.current && stream) {
            // Rebind the live stream to this (possibly freshly-remounted)
            // element via the service, which also points the capture path at
            // the current <video>. We deliberately do NOT re-acquire the
            // camera on remount — that's what made iOS re-prompt + error.
            cameraService.setVideoElement(videoRef.current);
            videoRef.current.play().catch(() => {});
        }
        const ready = !!stream && isReady;

        // THE FINAL FIX IS HERE:
        // Only update the parent if the ready status has ACTUALLY changed.
        if (ready !== cameraReady) {
            setCameraReady(ready);
        }

    }, [stream, isReady, setCameraReady, cameraReady]); // <-- Add cameraReady to the end

    // Effect 5: intentionally does NOT stop the camera on unmount.
    //
    // BackgroundCamera unmounts every time the user moves to a non-camera
    // screen (result, voice-note, organization, …) and remounts on return.
    // Stopping the tracks here meant each return triggered a fresh
    // getUserMedia, which on iOS re-shows the permission prompt and often
    // errors on the rapid re-acquire — the reported "asks again + error page"
    // bug. We keep the stream alive across these transitions (Effect 4
    // rebinds it to the new element) and only fully release it on logout
    // (see userSlice.logout → cameraStore.stopCamera).

    // Effect 6: Auto-resume if the video is paused while a stream is still
    // active. iOS Safari (and some Android browsers) pause <video> elements
    // across DOM transitions (e.g. the post-login auth flip) and crucially
    // require a user gesture to call play() again — effect-driven retries
    // get silently rejected. We listen for:
    //   - pause/visibilitychange/focus (wake events the browser gives us for
    //     free if it cooperates);
    //   - and more importantly click/touchend on document, which guarantees
    //     the play() call runs inside a user gesture. First tap anywhere
    //     after login resumes the feed.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const tryResume = () => {
            if (stream && video.paused) {
                video.play().catch(() => { /* ignore autoplay rejection */ });
            }
        };

        video.addEventListener('pause', tryResume);
        document.addEventListener('visibilitychange', tryResume);
        window.addEventListener('focus', tryResume);
        document.addEventListener('click', tryResume, { passive: true, capture: true });
        document.addEventListener('touchend', tryResume, { passive: true, capture: true });

        return () => {
            video.removeEventListener('pause', tryResume);
            document.removeEventListener('visibilitychange', tryResume);
            window.removeEventListener('focus', tryResume);
            document.removeEventListener('click', tryResume, { capture: true });
            document.removeEventListener('touchend', tryResume, { capture: true });
        };
    }, [stream]);

    return (
        <>
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                    transform: facingMode === 'user' ? 'scaleX(-1)' : 'none', // Only mirror selfie camera
                    zIndex: 0,
                    backgroundColor: 'transparent'
                }}
            />
            {(!isReady || cameraError) && (
                <div
                    className="absolute inset-0 w-full h-full"
                    style={{
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
                        backgroundSize: '400% 400%',
                        animation: 'gradientShift 8s ease-in-out infinite',
                        zIndex: 1
                    }}
                />
            )}
            {SHOW_DEBUG_BADGE && (
                <div
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 99999,
                        background: 'rgba(0,0,0,0.75)',
                        color: '#fbbf24',
                        fontFamily: 'monospace',
                        fontSize: 10,
                        padding: '4px 6px',
                        borderRadius: 4,
                        lineHeight: 1.3,
                        pointerEvents: 'none'
                    }}
                >
                    BG mounted<br/>
                    stream: {stream ? 'Y' : 'N'}<br/>
                    tracks: {stream ? stream.getVideoTracks().filter(t => t.readyState === 'live').length : 0}<br/>
                    ready: {isReady ? 'Y' : 'N'}<br/>
                    paused: {videoRef.current?.paused ? 'Y' : 'N'}<br/>
                    rs: {videoRef.current?.readyState ?? '-'}<br/>
                    t: {videoRef.current ? videoRef.current.currentTime.toFixed(1) : '-'}<br/>
                    vid: {videoRef.current ? `${videoRef.current.videoWidth}x${videoRef.current.videoHeight}` : '-'}<br/>
                    win: {typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '-'}<br/>
                    so.angle: {(() => { try { return window.screen?.orientation?.angle ?? '-'; } catch { return 'err'; } })()}<br/>
                    so.type: {(() => { try { return (window.screen?.orientation?.type ?? '-').replace('-primary','').replace('-secondary','²'); } catch { return 'err'; } })()}<br/>
                    w.ori: {typeof (window as Window & { orientation?: number }).orientation !== 'undefined' ? (window as Window & { orientation?: number }).orientation : '-'}<br/>
                    gyro: {(() => { const a = cameraService.getPhysicalAngle(); return a < 0 ? 'n/a' : a; })()}<br/>
                    err: {cameraError ? cameraError.slice(0, 30) : '-'}<br/>
                    <span style={{ color: '#fbbf24' }}>devs:</span> {availableDevices?.length ?? 0} (R:{rearDevices?.length ?? 0} F:{frontDevices?.length ?? 0})<br/>
                    {(availableDevices ?? []).slice(0, 4).map((d: MediaDeviceInfo, i: number) => (
                        <span key={i}>· {(d.label || '<no-label>').slice(0, 32)}<br/></span>
                    ))}
                </div>
            )}
            <style>{`
                @keyframes gradientShift {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
            `}</style>
        </>
    );
};

export default BackgroundCamera;
